import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, X, FileText, ExternalLink, ArrowUp, ArrowDown, Eye, EyeOff, Edit2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const KIND_OPTIONS = [
    { value: "ein", label: "EIN / Tax ID letter" },
    { value: "articles", label: "Articles of Incorporation" },
    { value: "bylaws", label: "Bylaws" },
    { value: "determination", label: "IRS 501(c)(3) determination" },
    { value: "990", label: "IRS Form 990" },
    { value: "annual-report", label: "Annual report" },
    { value: "policy", label: "Policy" },
    { value: "document", label: "Other document" },
];

const blankForm = { title: "", description: "", file_url: "", kind: "document", active: true };

const PublicRecordsPanel = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(blankForm);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/public-records");
            setRows(data || []);
        } catch (e) {
            toast.error("Could not load public records");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const reset = () => { setAdding(false); setEditingId(null); setForm(blankForm); };

    const startEdit = (r) => {
        setEditingId(r.id);
        setAdding(false);
        setForm({ title: r.title, description: r.description || "", file_url: r.file_url, kind: r.kind, active: r.active });
    };

    const save = async () => {
        if (!form.title.trim() || !form.file_url.trim()) {
            toast.error("Title and file URL are required."); return;
        }
        try {
            if (editingId) {
                await api.patch(`/admin/public-records/${editingId}`, form);
                toast.success("Record saved");
            } else {
                await api.post("/admin/public-records", form);
                toast.success("Record added");
            }
            reset();
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const uploadAndFill = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category_slug", "misc");
        try {
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const url = data?.url || data?.file_url || data?.image_url;
            if (url) {
                setForm((p) => ({ ...p, file_url: url, title: p.title || file.name.replace(/\.[^.]+$/, "") }));
                toast.success("Uploaded — URL filled in");
            } else {
                toast.error("Upload returned no URL");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        }
    };

    const toggleActive = async (r) => {
        try {
            await api.patch(`/admin/public-records/${r.id}`, { active: !r.active });
            await load();
        } catch (e) {
            toast.error("Update failed");
        }
    };

    const remove = async (r) => {
        if (!window.confirm(`Delete "${r.title}"? Visitors will no longer see this document.`)) return;
        try {
            await api.delete(`/admin/public-records/${r.id}`);
            toast.success("Deleted");
            await load();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const reorder = async (idx, delta) => {
        const j = idx + delta;
        if (j < 0 || j >= rows.length) return;
        const a = rows[idx], b = rows[j];
        try {
            await api.post("/admin/public-records/reorder", {
                order: [{ id: a.id, order: b.order }, { id: b.id, order: a.order }],
            });
            await load();
        } catch (e) {
            toast.error("Reorder failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="public-records-panel">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight">Public records & PDFs</h2>
                    <p className="text-sm text-[#4A5568] mt-1 max-w-2xl">
                        Foundational documents made downloadable on the public <code>/financials</code> page — EIN letter, Articles of Incorporation, bylaws, IRS determination letter, annual reports. Visitors can verify our nonprofit standing in one click.
                    </p>
                </div>
                <button
                    onClick={() => { setAdding(true); setEditingId(null); setForm(blankForm); }}
                    data-testid="public-record-add-btn"
                    className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium transition"
                >
                    <Plus className="w-4 h-4" strokeWidth={2} /> Add record
                </button>
            </div>

            {(adding || editingId) && (
                <div className="bg-white border border-[#E53935]/30 rounded-2xl p-5 space-y-3" data-testid="public-record-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Title</label>
                            <input data-testid="public-record-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" placeholder="EIN Confirmation Letter" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Document type</label>
                            <select data-testid="public-record-kind-select" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40">
                                {KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Description (optional)</label>
                        <textarea data-testid="public-record-description-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" placeholder="One-line caption shown under the title on the public page" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">File URL (PDF preferred)</label>
                        <div className="flex flex-col sm:flex-row gap-2 mt-1">
                            <input data-testid="public-record-url-input" value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })} className="flex-1 rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] font-mono text-xs" placeholder="https://… or upload below" />
                            <label className="inline-flex items-center gap-1 px-4 py-2.5 rounded-xl border border-black/10 hover:bg-[#F4F1EA] text-sm font-medium cursor-pointer whitespace-nowrap">
                                <FileText className="w-4 h-4" strokeWidth={2} /> Upload PDF
                                <input type="file" accept=".pdf,application/pdf,image/*" data-testid="public-record-file-input" className="hidden" onChange={(e) => uploadAndFill(e.target.files?.[0])} />
                            </label>
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-[#1C252E]">
                        <input type="checkbox" data-testid="public-record-active-checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Visible on public page
                    </label>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button onClick={reset} className="text-sm text-[#4A5568] hover:text-[#1C252E] inline-flex items-center gap-1"><X className="w-4 h-4" strokeWidth={2} /> Cancel</button>
                        <button onClick={save} data-testid="public-record-save-btn" className="inline-flex items-center gap-1 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2 text-sm font-medium"><Save className="w-4 h-4" strokeWidth={2} /> {editingId ? "Save changes" : "Add record"}</button>
                    </div>
                </div>
            )}

            {loading ? (
                <p className="text-sm text-[#4A5568]">Loading…</p>
            ) : rows.length === 0 ? (
                <p className="text-sm text-[#4A5568] italic" data-testid="public-records-empty">No public records yet.</p>
            ) : (
                <ul className="space-y-2" data-testid="public-records-list">
                    {rows.map((r, idx) => (
                        <li key={r.id} data-testid={`public-record-row-${r.id}`} className={`p-4 rounded-2xl border ${r.active ? "bg-white border-black/10" : "bg-[#F4F1EA]/50 border-black/5 opacity-70"}`}>
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#F2D8D5] text-[#E53935] shrink-0"><FileText className="w-3.5 h-3.5" strokeWidth={2} /></span>
                                    <div className="min-w-0">
                                        <p className="font-medium text-[#1C252E] truncate">{r.title}</p>
                                        <p className="text-xs text-[#4A5568] truncate">{r.description || (KIND_OPTIONS.find((k) => k.value === r.kind)?.label || r.kind)}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <a href={r.file_url} target="_blank" rel="noopener noreferrer" data-testid={`public-record-open-${r.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="Open file">
                                        <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                                    </a>
                                    <button onClick={() => reorder(idx, -1)} disabled={idx === 0} data-testid={`public-record-up-${r.id}`} aria-label="Move up" className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA] disabled:opacity-30"><ArrowUp className="w-4 h-4" strokeWidth={1.5} /></button>
                                    <button onClick={() => reorder(idx, +1)} disabled={idx === rows.length - 1} data-testid={`public-record-down-${r.id}`} aria-label="Move down" className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA] disabled:opacity-30"><ArrowDown className="w-4 h-4" strokeWidth={1.5} /></button>
                                    <button onClick={() => toggleActive(r)} data-testid={`public-record-toggle-${r.id}`} aria-label={r.active ? "Hide" : "Show"} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]">{r.active ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}</button>
                                    <button onClick={() => startEdit(r)} data-testid={`public-record-edit-${r.id}`} aria-label="Edit" className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]"><Edit2 className="w-4 h-4" strokeWidth={1.5} /></button>
                                    <button onClick={() => remove(r)} data-testid={`public-record-delete-${r.id}`} aria-label="Delete" className="p-1.5 rounded-full text-[#4A5568] hover:text-[#E53935] hover:bg-[#E53935]/10"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default PublicRecordsPanel;
